import { ArrowLeft, BoxSelect, ChevronDown } from "lucide-react";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import styles from "../../_css/RoleplayManagement.module.css";

const ScenarioPreview = () => {
  const [activeTab, setActiveTab] = useState("Scenario");
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Extract scenario data and the dashboard's returnState
  const scenarioData = location.state?.scenarioData;
  const returnState = location.state?.returnState;

  // Fallback data if accessed directly without state
  const displayData = scenarioData || {
    title: "Difficult Employee Conversation",
    description: "...",
    goals: [],
    competencies: [],
    image: null,
  };

  const parsedGoals = Array.isArray(displayData.goals)
    ? displayData.goals
    : displayData.goals?.split("\n").filter((goal) => goal.trim()) || [];

  const dropdowns = [
    "Conversation Tone",
    "Your Role",
    "Your Partner Role",
    "Simulation",
  ];

  const handleBackButtonClick = () => {
    // If we came from Edit, go back to Edit while preserving the returnState
    if (location.state?.from === "editScenario") {
      navigate("/roleplayManagement/editscenario", {
        state: {
          scenarioId: displayData.id,
          // Re-pass the dashboard state back to EditScenario
          ...returnState
        },
      });
    }
    // If we came from Add, go back to Add
    else if (location.state?.from === "addScenario") {
      navigate("/roleplayManagement/addScenario", {
        state: {
          scenarioId: displayData.id,
          ...returnState
        },
      });
    }
    // Otherwise, go directly to the Role Manager using the preserved filters
    else {
      navigate("/roleplayManagement", {
        state: returnState,
      });
    }
  };

  return (
    <div className={styles.scenarioPreviewContainer}>
      {/* Header */}
      <div className={styles.scenarioPreviewHeader}>
        <button
          onClick={handleBackButtonClick}
          className={styles.scenarioPreviewBackButton}
        >
          <ArrowLeft size={20} color="#171717" />
        </button>
        <h1 className={`font-clash-display ${styles.scenarioPreviewTitle}`}>
          Scenario Preview
        </h1>
      </div>

      {/* Content Grid */}
      <div className={styles.scenarioPreviewGrid}>
        {/* Left Panel */}
        <div className={styles.scenarioPreviewLeftPanel}>
          <div className={styles.scenarioPreviewContentSection}>
            <h2 className={styles.scenarioPreviewContentTitle}>
              {displayData.subCategories}
            </h2>

            <span className={styles.scenarioPreviewContentLabel}>
              📝 Scenario: {displayData.scenarioShortDescription}
            </span>

            <p className={styles.scenarioPreviewContentDescription}>
              {displayData.scenarioDescription}
            </p>
          </div>

          {/* Learning Goals */}
          <div className={styles.scenarioPreviewContentSection}>
            <span className={styles.scenarioPreviewSectionTitle}>
              🎯 Key Learning Goals:
            </span>
            <ul className={styles.scenarioPreviewList}>
              {parsedGoals.map((goal, index) => (
                <li key={index} className={styles.scenarioPreviewListItem}>
                  <span className={styles.scenarioPreviewListBullet}>•</span>
                  {goal}
                </li>
              ))}
            </ul>
          </div>

          {/* Trained Competences */}
          <div className={styles.scenarioPreviewContentSection}>
            <span className={styles.scenarioPreviewSectionTitle}>
              🧠 Trained Competences:
            </span>
            <ul className={styles.scenarioPreviewList}>
              {displayData.competencies?.map((skill, index) => (
                <li key={index} className={styles.scenarioPreviewListItem}>
                  <span className={styles.scenarioPreviewListBullet}>•</span>
                  {skill}
                </li>
              ))}
            </ul>
          </div>

          {/* Image Box */}
          <div className={styles.scenarioPreviewImageBox}>
            {displayData.image ? (
              <img
                src={
                  displayData.image.startsWith("data:") ||
                    displayData.image.includes("http")
                    ? displayData.image
                    : `${process.env.PUBLIC_URL}/img/${displayData.image}.png`
                }
                alt="Scenario"
                onError={(e) => {
                  e.target.src = "placeholder-image-url"; // Optional fallback
                }}
              />
            ) : (
              "No Image Found!"
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className={styles.scenarioPreviewRightPanel}>
          <div className={styles.scenarioPreviewTabWrapper}>
            {["Scenario", "Avatars"].map((tab) => (
              <button
                key={tab}
                className={`${styles.scenarioPreviewTabButton} ${activeTab === tab
                  ? styles.scenarioPreviewTabButtonActive
                  : styles.scenarioPreviewTabButtonInactive
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {dropdowns.map((label, index) => (
            <div key={index}>
              <label className={styles.scenarioPreviewDropdownLabel}>
                {label}
              </label>
              <div className={styles.scenarioPreviewSelectWrapper}>
                <select className={styles.scenarioPreviewSelect}>
                  <option>Select</option>
                </select>
                <div className={styles.scenarioPreviewSelectChevron}>
                  <ChevronDown size={18} />
                </div>
              </div>
            </div>
          ))}

          <hr className={styles.scenarioPreviewDivider} />
          <button className={styles.scenarioPreviewButton}>PractAIce</button>
        </div>
      </div>
    </div>
  );
};

export default ScenarioPreview;